import { 
  getPriceReport, 
  getMarketCapReport, 
  getTopByMarketCap,
  getSignificantPriceMovements,
  getTotalSubnetPrice,
  getInvestmentAnalysis,
  analyzeSubnetPotential
} from './reports/marketReports';
import { 
  getHolderReport, 
  getTopByHolders,
  getHolderDistributionForTimeRange 
} from './reports/holderReports';
import { getMindshareMovement } from './reports/mindshareReports';
import { getTwitterReport } from './reports/twitterReports';
import { getEmissionsReport } from './reports/emissionsReports';
import { 
  getWhaleBuyingReport,
  getWhaleSellingReport,
  getSellingPressureReport,
  getTopDelegatorsReport,
  getSilentAccumulationReport
} from './reports/delegationReports';
import { 
  extractTimeRange, 
  extractListParams, 
  extractSubnets,
  findCompanySubnets,
  getRecentTweets,
  extractKeywords
} from './reports/utils';
import { subnetNames } from '../utils/subnetNames';
import { subnetDescriptions } from '../utils/subnetDescriptions';
import { formatDistanceToNow } from 'date-fns';
import { generateCompanyAnalysis, answerQuestion, generateMarketMovementAnalysis } from './openaiService';

export async function generateSubnetReport(query: string): Promise<string> {
  try {
    // Check for delegation/whale activity queries
    const whaleBuyingMatch = query.toLowerCase().match(/(?:what|which|where).*(?:whale|whales|big money|large holders|big players).*(?:buy|buying|invest|investing|delegate|delegating)/i);
    const whaleSellingMatch = query.toLowerCase().match(/(?:what|which|where).*(?:whale|whales|big money|large holders|big players).*(?:sell|selling|dump|dumping|undelegate|undelegating)/i);
    const sellingPressureMatch = query.toLowerCase().match(/(?:what|which|where).*(?:sell|selling|dump|dumping|undelegate|undelegating)/i);
    const delegatorMatch = query.toLowerCase().match(/(?:who|which|what).*(?:wallet|wallets|address|addresses|delegator|delegators)/i);
    const silentAccumulationMatch = query.toLowerCase().match(/(?:what|which|where).*(?:whale|whales|big money|large holders|big players).*(?:silent|quietly|accumulate|accumulating|stealth)/i);
    
    // Handle silent accumulation queries
    if (silentAccumulationMatch || 
        query.toLowerCase().includes('silently accumulating') || 
        query.toLowerCase().includes('quietly buying')) {
      const { hours, description } = extractTimeRange(query);
      return getSilentAccumulationReport(hours);
    }
    
    // Handle whale buying queries
    if (whaleBuyingMatch || (query.toLowerCase().includes('whale') && !query.toLowerCase().includes('sell'))) {
      const { hours, description } = extractTimeRange(query);
      return getWhaleBuyingReport(hours);
    }
    
    // Handle whale selling queries
    if (whaleSellingMatch || query.toLowerCase().includes('whales selling') || query.toLowerCase().includes('what are whales selling')) {
      const { hours, description } = extractTimeRange(query);
      return getWhaleSellingReport(hours);
    }
    
    // Handle general selling pressure queries
    if (sellingPressureMatch && !query.toLowerCase().includes('whale')) {
      const { hours, description } = extractTimeRange(query);
      return getSellingPressureReport(hours);
    }
    
    // Handle delegator/wallet queries
    if (delegatorMatch) {
      const { hours, description } = extractTimeRange(query);
      return getTopDelegatorsReport(hours);
    }

    // Check for emissions query
    const emissionsMatch = query.toLowerCase().match(/(?:what'?s?|what is|show|get).*emissions.*(?:for |of )?(\w+)/i);
    if (emissionsMatch || query.toLowerCase().includes('emissions')) {
      if (query.toLowerCase().includes('chutes emissions')) {
        return getEmissionsReport(64);
      }
      
      // Try to extract subnet from the query if not already found
      const subnets = extractSubnets(query);
      if (subnets.length === 1) {
        return getEmissionsReport(subnets[0]);
      }
      
      const subnetName = emissionsMatch?.[1]?.toLowerCase();
      if (subnetName) {
        const matchingSubnet = Object.entries(subnetNames).find(([_, name]) => 
          name.toLowerCase() === subnetName
        );
        if (matchingSubnet) {
          return getEmissionsReport(parseInt(matchingSubnet[0]));
        }
      }
      
      return "Please specify which subnet you'd like emissions data for.";
    }

    // Extract subnets first to determine if this is a subnet query
    const subnets = extractSubnets(query);
    const isSubnetQuery = subnets.length > 0 || query.toLowerCase().includes('subnet');

    // Extract keywords with appropriate stop word filtering
    const keywords = extractKeywords(query, isSubnetQuery);

    // Check for potential/promising subnets query
    const potentialMatch = query.toLowerCase().match(/(?:show|what|which|list).*(?:subnet|subnets).*(?:potential|promising|good|interesting|worth|watching)/i);
    if (potentialMatch) {
      const subnets = Object.keys(subnetNames).map(id => parseInt(id));
      const potentialSubnets = await Promise.all(
        subnets.map(netuid => analyzeSubnetPotential(netuid))
      );

      // Filter out null results and sort by score
      const validSubnets = potentialSubnets
        .filter(s => s !== null)
        .sort((a, b) => b!.score - a!.score);

      if (validSubnets.length === 0) {
        return "No subnets currently showing significant potential.";
      }

      // For the specific query "What subnets are showing potential", return a bulleted list
      if (query.toLowerCase().includes("what subnets are showing potential") || 
          query.toLowerCase().includes("subnets showing potential") ||
          query.toLowerCase().includes("subnets with potential")) {
        
        let response = "";
        
        // Take top 5 subnets
        const subnetPromises = validSubnets.slice(0, 5).map(async (subnet) => {
          // Get subnet description
          const subnetInfo = subnetDescriptions[subnet!.netuid];
          const description = subnetInfo?.description || '';
          
          // Get recent tweets for this subnet
          const tweets = await getRecentTweets(subnet!.netuid);
          const recentTweets = tweets.slice(0, 3).map(t => t.text).join('\n');
          
          // Prepare bullet points
          let bulletPoints = [];
          
          // What they're building (from tweets or description)
          if (subnet!.buildingProgress) {
            bulletPoints.push(`Building ${subnet!.buildingProgress.trim()}`);
          } else {
            const buildingMatch = description.match(/(?:building|developing|creating|focuses on|platform for|network for|system for|framework for) ([^.]+)/i);
            if (buildingMatch) {
              bulletPoints.push(`Building ${buildingMatch[1].trim()}`);
            }
          }
          
          // Price movement
          if (subnet!.priceChange24h !== 0) {
            const priceDirection = subnet!.priceChange24h > 0 ? 'up' : 'down';
            bulletPoints.push(`Price ${priceDirection} ${Math.abs(subnet!.priceChange24h).toFixed(1)}% in 24h`);
          }
          
          // Add one more key insight
          if (subnet!.buyPressure > 0.6) {
            bulletPoints.push(`Strong buy pressure at ${(subnet!.buyPressure * 100).toFixed(1)}%`);
          } else if (subnet!.holderChange24h > 5) {
            bulletPoints.push(`Holder growth of ${subnet!.holderChange24h.toFixed(1)}% in 24h`);
          } else if (subnet!.volumeChange24h > 20) {
            bulletPoints.push(`Volume increased by ${subnet!.volumeChange24h.toFixed(1)}%`);
          } else if (subnet!.twitterSentiment === 'bullish') {
            bulletPoints.push(`Bullish social media sentiment`);
          }
          
          // Generate a summary using OpenAI
          const bulletPointsText = bulletPoints.join('\n');
          const context = `
            Subnet: ${subnet!.name} (SN${subnet!.netuid})
            Description: ${description}
            
            Bullet points:
            ${bulletPointsText}
            
            Recent tweets:
            ${recentTweets}
            
            IMPORTANT INSTRUCTIONS:
            1. Focus ONLY on ${subnet!.name} (SN${subnet!.netuid}) in your summary
            2. DO NOT mention any other subnets in your response
            3. DO NOT mention whale wallets, whale buys, or net inflow in your summary
            4. Focus on what they're building, price action, and development progress
            5. Keep it concise and focused on the technology and market performance
            6. Write 3-5 sentences maximum
          `;
          
          // Generate a concise summary
          const summary = await generateMarketMovementAnalysis(context);
          
          // Format the response
          return {
            name: subnet!.name,
            netuid: subnet!.netuid,
            summary: summary
          };
        });
        
        const subnetSummaries = await Promise.all(subnetPromises);
        
        // Format the final response
        subnetSummaries.forEach(item => {
          response += `- ${item.name} (SN${item.netuid})\n\n`;
          response += `  ${item.summary}\n\n`;
        });
        
        return response;
      }

      // For other potential subnet queries, use the original detailed format
      const analysisData = validSubnets.map(subnet => ({
        name: subnet!.name,
        netuid: subnet!.netuid,
        description: subnetDescriptions[subnet!.netuid]?.description || '',
        metrics: {
          priceChange: subnet!.priceChange24h,
          volumeChange: subnet!.volumeChange24h,
          holderChange: subnet!.holderChange24h,
          buyPressure: subnet!.buyPressure,
        },
        reasons: subnet!.reasons,
        recentUpdates: subnet!.recentTweets
      }));

      // Generate analysis for each subnet
      const analyses = await Promise.all(
        analysisData.slice(0, 5).map(async subnet => {
          const context = `
            Subnet: ${subnet.name} (SN${subnet.netuid})
            Description: ${subnet.description}
            Price Change 24h: ${subnet.metrics.priceChange.toFixed(2)}%
            Volume Change 24h: ${subnet.metrics.volumeChange.toFixed(2)}%
            Holder Change 24h: ${subnet.metrics.holderChange.toFixed(2)}%
            Buy Pressure: ${(subnet.metrics.buyPressure * 100).toFixed(2)}%
            Key Points: ${subnet.reasons.join(', ')}
            Recent Updates: ${subnet.recentUpdates.join(' | ')}
            
            IMPORTANT INSTRUCTIONS:
            1. Focus ONLY on ${subnet.name} (SN${subnet.netuid}) in your analysis
            2. DO NOT mention any other subnets in your response
            3. DO NOT mention whale wallets, whale buys, or net inflow in your analysis
            4. Focus on what they're building, price action, and development progress
          `;

          const analysis = await generateMarketMovementAnalysis(context);
          return `${subnet.name} (SN${subnet.netuid}): ${analysis}`;
        })
      );

      return analyses.join('\n\n');
    }

    // Check for subnet news query
    const newsMatch = query.toLowerCase().match(/(?:what|show|get|any).*(?:news|updates|happening|activity)/i);
    if (newsMatch) {
      const { hours } = extractTimeRange(query);
      
      // If specific subnets mentioned, get their news
      if (subnets.length > 0) {
        const reports = await Promise.all(
          subnets.map(async (netuid) => {
            const tweets = await getRecentTweets(netuid, query);
            if (tweets.length === 0) return null;
            
            const tweetTexts = tweets.map(tweet => tweet.text).join('\n');
            const analysis = await generateMarketMovementAnalysis(tweetTexts);
            
            return `${subnetNames[netuid]} (SN${netuid}): ${analysis}`;
          })
        );
        
        const validReports = reports.filter(r => r !== null);
        if (validReports.length > 0) {
          return validReports.join('\n\n');
        }
      }
      
      // If no specific subnets or no news found, get general subnet activity
      const tweets = await getRecentTweets('all', query);
      if (tweets.length > 0) {
        const tweetTexts = tweets.map(tweet => tweet.text).join('\n');
        return await generateMarketMovementAnalysis(tweetTexts);
      }
    }

    // Check for gainers/losers query
    const gainersMatch = query.toLowerCase().match(/(?:top|best|highest|biggest|show|list|get)\s+(?:\d+|[a-z]+)?\s*(?:gain|gainer|gainers|movers|price|change)/i);
    const losersMatch = query.toLowerCase().match(/(?:top|worst|lowest|biggest|show|list|get)\s+(?:\d+|[a-z]+)?\s*(?:loss|loser|losers|drop|decline)/i);
    
    if (gainersMatch || losersMatch) {
      const { count } = extractListParams(query);
      const type = losersMatch ? 'losers' : 'gainers';
      return getTopByMarketCap(count, type);
    }

    // Check for investment analysis queries
    const investmentMatch = query.match(/(?:is|should|worth|good).*(?:buy|invest|entry|investment)/i);
    if (investmentMatch) {
      const subnets = extractSubnets(query);
      if (subnets.length === 1) {
        return getInvestmentAnalysis(subnets[0]);
      }
    }

    // Check for total price query
    if (query.toLowerCase().includes('total subnet price') ||
        query.toLowerCase().includes('total price of subnets') ||
        query.toLowerCase().includes('sum of subnet prices') ||
        query.toLowerCase().includes('all subnet prices')) {
      return getTotalSubnetPrice();
    }

    // Check for general market movement query
    if (query.toLowerCase().includes('price movement') || 
        query.toLowerCase().includes('market movement') ||
        query.toLowerCase().includes('moving')) {
      const { hours } = extractTimeRange(query);
      return getSignificantPriceMovements(hours);
    }

    // Check for specific market movement related terms
    const marketMovementMatch = query.match(/(?:why|what|how).+(?:pump|dump|moon|crash|drop|rise|up|down|sell|buy)/i);
    if (marketMovementMatch) {
      const subnets = extractSubnets(query);
      if (subnets.length === 1) {
        const netuid = subnets[0];
        const { hours } = extractTimeRange(query);
        
        const [holderData, tweets] = await Promise.all([
          getHolderDistributionForTimeRange(netuid, hours),
          getRecentTweets(netuid, query)
        ]);
        
        let report = `Market Movement Analysis for ${subnetNames[netuid]}\n\n`;
        
        if (holderData) {
          report += `${holderData}\n\n`;
        }
        
        if (tweets.length > 0) {
          report += 'Recent Updates:\n';
          tweets.slice(0, 3).forEach(tweet => {
            const tweetDate = formatDistanceToNow(tweet.created_at, { addSuffix: true });
            report += `${tweetDate}: ${tweet.text}\n`;
          });
        }
        
        return await generateMarketMovementAnalysis(report);
      }
    }

    // Check for direct price query
    const priceMatch = query.toLowerCase().match(/(?:what'?s?|what is|show|get) (?:the )?price (?:of |for )?(\w+)/i);
    if (priceMatch || query.toLowerCase().includes('price')) {
      // If we have explicit subnets extracted, use the first one
      if (subnets.length === 1) {
        return getPriceReport(subnets[0]);
      }
      
      // Try to extract subnet name from the query
      if (priceMatch) {
        const subnetName = priceMatch[1].toLowerCase();
        // Look for exact match in subnet names
        const matchingSubnet = Object.entries(subnetNames).find(([_, name]) => 
          name.toLowerCase() === subnetName
        );
        
        if (matchingSubnet) {
          return getPriceReport(parseInt(matchingSubnet[0]));
        }
        
        // Look for partial match in subnet names
        const partialMatch = Object.entries(subnetNames).find(([_, name]) => 
          name.toLowerCase().includes(subnetName)
        );
        
        if (partialMatch) {
          return getPriceReport(parseInt(partialMatch[0]));
        }
      }
    }

    // Check for company info request
    const companyMatch = findCompanySubnets(query);
    if (companyMatch) {
      const { companyName, subnets } = companyMatch;
      const tweets = await getRecentTweets(companyName, query);
      
      if (query.includes('?')) {
        if (tweets.length > 0) {
          const tweetTexts = tweets.map(tweet => 
            `${formatDistanceToNow(tweet.created_at, { addSuffix: true })}: ${tweet.text}`
          ).join('\n');
          
          return await answerQuestion(query, tweetTexts);
        }
        return "No recent information available.";
      }
      
      let report = `${companyName.toUpperCase()}\n\n`;
      
      if (tweets.length > 0) {
        const analysis = await generateCompanyAnalysis(tweets.map(t => t.text).join('\n'));
        report += `${analysis}\n\n`;
      }
      
      report += 'Associated Subnets:\n';
      subnets.forEach(netuid => {
        report += `${subnetNames[netuid]} SN${netuid}\n`;
      });
      
      if (tweets.length > 0) {
        report += '\nRecent Updates:\n';
        tweets.slice(0, 3).forEach(tweet => {
          const tweetDate = formatDistanceToNow(tweet.created_at, { addSuffix: true });
          report += `${tweetDate}: ${tweet.text}\n`;
        });
      }
      
      return report;
    }

    // For any other query, search through Twitter data
    if (subnets.length === 1) {
      const netuid = subnets[0];
      const tweets = await getRecentTweets(netuid, query);
      
      if (tweets.length > 0) {
        const tweetTexts = tweets.map(tweet => 
          `${formatDistanceToNow(tweet.created_at, { addSuffix: true })}: ${tweet.text}`
        ).join('\n');
        
        const subnetContext = `${subnetNames[netuid]} SN${netuid}\n` +
          `${subnetDescriptions[netuid]?.description || 'No description available.'}\n\n`;
        
        return await answerQuestion(query, subnetContext + tweetTexts);
      }
    }

    // If no specific subnet mentioned, search across all tweets
    const tweets = await getRecentTweets('all', query);
    if (tweets.length > 0) {
      const tweetTexts = tweets.map(tweet => 
        `${formatDistanceToNow(tweet.created_at, { addSuffix: true })}: ${tweet.text}`
      ).join('\n');
      
      return await answerQuestion(query, tweetTexts);
    }

    return "No relevant information found. Try asking about a specific subnet or company, or try these examples:\n\n" +
           "What subnets are whales buying?\n" +
           "What subnets are whales selling?\n" +
           "Which subnets are seeing selling pressure?\n" +
           "Who are the top active wallets?\n" +
           "What subnets are whales silently accumulating?\n" +
           "What subnets are showing potential?\n" +
           "Show me subnet news from today\n" +
           "What is the price of Chutes?\n" +
           "Why is BitMind dropping?\n" +
           "Compare market caps between Subnet 1 and 2\n" +
           "List top 5 subnets by market cap\n" +
           "Show holder distribution for Subnet 64\n" +
           "Show mindshare movement for Subnet 3\n" +
           "Tell me about Rayon Labs\n" +
           "What updates does Subnet 64 have?";
  } catch (error) {
    console.error('Error processing query:', error);
    return 'Unable to process your query. Please try again.';
  }
}